import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, IconButton, Chip } from '@mui/material';
import { Close as CloseIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';

/**
 * 頂部橫幅式提醒組件
 * @param {string} message - 提示訊息
 * @param {boolean} show - 是否顯示（預設 true）
 * @param {function} onClose - 關閉回調
 * @param {string} severity - 嚴重程度：'info' | 'success' | 'warning' | 'error'（預設 'info'）
 * @param {string} title - 標題（預設 '小提醒'）
 * @param {string} chipLabel - Chip 標籤文字（可選）
 * @param {string} mascotImage - 吉祥物圖片路徑（可選）
 */
export default function TopBannerGuidance({ 
  message, 
  show = true, 
  onClose, 
  severity = 'info',
  title = '小提醒',
  chipLabel,
  mascotImage
}) {
  const [isVisible, setIsVisible] = useState(show);

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  };

  if (!isVisible) return null;

  // 根據 severity 設定顏色
  const getSeverityStyles = () => {
    switch (severity) {
      case 'success':
        return {
          background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)',
          borderColor: '#a5d6a7',
          titleColor: '#2e7d32'
        };
      case 'warning':
        return {
          background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
          borderColor: '#ffcc80',
          titleColor: '#e65100'
        };
      case 'error':
        return {
          background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
          borderColor: '#ef9a9a',
          titleColor: '#c62828'
        };
      default: // info
        return {
          background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
          borderColor: '#90caf9',
          titleColor: '#1565c0'
        };
    }
  };

  const styles = getSeverityStyles();

  return (
    <Card 
      sx={{ 
        mb: 3, 
        overflow: 'hidden', 
        background: styles.background,
        border: `1px solid ${styles.borderColor}`,
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}
    >
      <CardContent sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, 
        gap: { xs: 2, md: 3 }, 
        alignItems: { xs: 'flex-start', md: 'center' },
        py: { xs: 2, md: 2.5 },
        position: 'relative'
      }}>
        {mascotImage && (
          <Box
            component="img"
            src={mascotImage}
            alt="吉祥物"
            sx={{
              width: { xs: '120px', md: '140px' },
              height: 'auto',
              objectFit: 'contain',
              flexShrink: 0
            }}
          />
        )}
        <Box sx={{ flex: 1, width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: chipLabel ? 1.5 : 0.5 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                color: styles.titleColor,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              {title}
            </Typography>
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{ 
                color: '#757575',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                ml: 1,
                padding: '4px'
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'text.secondary', 
              fontSize: { xs: '0.875rem', md: '0.9375rem' },
              lineHeight: 1.6,
              mb: chipLabel ? 1.5 : 0
            }}
          >
            {message}
          </Typography>
          {chipLabel && (
            <Chip
              icon={<CheckCircleIcon />}
              label={chipLabel}
              color={severity === 'success' ? 'success' : 'default'}
              sx={{ 
                px: 1.5, 
                py: 0.5, 
                fontWeight: 'bold',
                fontSize: { xs: '0.75rem', md: '0.8125rem' },
                height: 'auto'
              }}
              variant={severity === 'success' ? 'filled' : 'outlined'}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
}


